<script setup>
import { computed, onMounted, ref } from 'vue';
import ReportHeader from './components/ReportHeader.vue';
import MetricCard from './components/overview/MetricCard.vue';
import SzKpiRow from './components/overview/SzKpiRow.vue';
import SzHorizontalBarChart from './components/overview/SzHorizontalBarChart.vue';
import SzOverviewLineChart from './components/overview/SzOverviewLineChart.vue';
import SzTokensCard from './components/overview/SzTokensCard.vue';
import SzBotsCard from './components/overview/SzBotsCard.vue';
import SzEtiquetasCard from './components/overview/SzEtiquetasCard.vue';
import SzForceGraph from './components/overview/SzForceGraph.vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import Report from 'dashboard/api/reports';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import subDays from 'date-fns/subDays';

const store = useStore();

// Vuex getters
const conversationHeatmap = useMapGetter('getAccountConversationHeatmapData');
const resolutionHeatmap = useMapGetter('getAccountResolutionHeatmapData');
const agents = useMapGetter('agents/getAgents');
const agentConversationMetric = useMapGetter('getAgentConversationMetric');
const teams = useMapGetter('teams/getTeams');
const teamConversationMetric = useMapGetter('getTeamConversationMetric');
const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabels');

// API-fetched data
const agentReports = ref([]);
const inboxReports = ref([]);
const accountSummary = ref({ current: {}, previous: {} });


function aggregateByHour(heatmapArr) {
  const hours = new Array(24).fill(0);
  if (!Array.isArray(heatmapArr)) return hours;
  heatmapArr.forEach(item => {
    if (item && typeof item.timestamp === 'number') {
      const hour = new Date(item.timestamp * 1000).getHours();
      hours[hour] += (item.value || 0);
    }
  });
  return hours;
}

const conversationData = computed(() => aggregateByHour(conversationHeatmap.value));
const resolutionData = computed(() => aggregateByHour(resolutionHeatmap.value));

// Agent conversations bar chart (real Vuex data)
const agentItems = computed(() => {
  const metrics = agentConversationMetric.value || [];
  return metrics
    .filter(m => m.assignee_id || m.id)
    .map(m => {
      const agentId = m.assignee_id || m.id;
      const agent = agents.value.find(a => a.id === agentId) || {};
      return {
        name: agent.available_name || agent.name || 'Agent ' + agentId,
        thumbnail: agent.thumbnail || '',
        values: [
          { label: 'Não atendidas', value: m.unattended || 0, color: '#2C7B2C' },
          { label: 'Abertas', value: (m.open || 0) - (m.unattended || 0), color: '#7DBA80' },
        ],
      };
    })
    .filter(i => i.values.some(v => v.value > 0))
    .sort((a, b) => {
      const ta = a.values.reduce((s, v) => s + v.value, 0);
      const tb = b.values.reduce((s, v) => s + v.value, 0);
      return tb - ta;
    })
    .slice(0, 8);
});

// Agent response time bar chart (real API data)
const responseTimeItems = computed(() => {
  return agentReports.value
    .filter(r => r.avg_first_response_time > 0)
    .sort((a, b) => a.avg_first_response_time - b.avg_first_response_time)
    .slice(0, 8)
    .map(r => ({
      name: r.name || 'Agent',
      thumbnail: r.thumbnail || '',
      values: [{ label: 'Minutos', value: Math.round(r.avg_first_response_time / 60), color: '#FF5500' }],
    }));
});

// Inbox volume bar chart (real API data)
const inboxVolumeData = computed(() => {
  return inboxReports.value
    .filter(r => r.conversations_count > 0)
    .sort((a, b) => b.conversations_count - a.conversations_count)
    .slice(0, 6)
    .map(r => ({
      name: r.name || 'Inbox',
      value: r.conversations_count || 0,
    }));
});
const inboxVolumeMax = computed(() => Math.max(...inboxVolumeData.value.map(i => i.value), 1));

const inboxYMax = computed(() => {
  const max = inboxVolumeMax.value;
  const step = max <= 10 ? 2 : max <= 50 ? 10 : max <= 200 ? 50 : max <= 1000 ? 250 : 500;
  return Math.ceil(max / step) * step + step;
});

const inboxYTicks = computed(() => {
  const max = inboxYMax.value;
  const step = max <= 10 ? 2 : max <= 50 ? 10 : max <= 200 ? 50 : max <= 1000 ? 250 : 500;
  const ticks = [];
  for (let i = max; i >= 0; i -= step) ticks.push(i);
  return ticks;
});

// Conversation duration per inbox (real API data)
const conversationDurationItems = computed(() => {
  return inboxReports.value
    .filter(r => r.avg_resolution_time > 0)
    .sort((a, b) => a.avg_resolution_time - b.avg_resolution_time)
    .slice(0, 6)
    .map(r => ({
      name: r.name || 'Inbox',
      thumbnail: '',
      noAvatar: true,
      values: [{ label: 'Minutos', value: Math.round(r.avg_resolution_time / 60), color: '#3B82F6' }],
    }));
});

function downloadCSV(filename, headers, rows) {
  const csv = headers.join(',') + '\n' + rows.map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename + '.csv'; a.click();
  URL.revokeObjectURL(url);
}

function exportResponseTime() {
  const rows = responseTimeItems.value.map(i => ['"' + i.name + '"', i.values[0]?.value || 0]);
  downloadCSV('tempo_medio_resposta', ['Agente', 'Minutos'], rows);
}

function exportAgentConversations() {
  const rows = agentItems.value.map(i => ['"' + i.name + '"', i.values[0]?.value || 0, i.values[1]?.value || 0]);
  downloadCSV('conversas_por_agente', ['Agente', 'Nao atendidas', 'Abertas'], rows);
}

function exportInboxVolume() {
  const rows = inboxVolumeData.value.map(i => ['"' + i.name + '"', i.value]);
  downloadCSV('volume_inbox', ['Caixa de entrada', 'Conversas'], rows);
}

function exportTrafego() {
  const data = conversationData.value || [];
  const hours = new Array(24).fill(0);
  data.forEach(item => {
    if (item && typeof item.timestamp === 'number') {
      const hour = new Date(item.timestamp * 1000).getHours();
      hours[hour] += (item.value || 0);
    }
  });
  const rows = hours.map((v, i) => [i + 'h', v]);
  downloadCSV('trafego_conversa', ['Hora', 'Conversas'], rows);
}

function exportDuration() {
  const rows = conversationDurationItems.value.map(i => ['"' + i.name + '"', i.values[0]?.value || 0]);
  downloadCSV('duracao_conversa', ['Inbox', 'Minutos'], rows);
}

async function fetchReportData() {
  const now = new Date();
  const since = getUnixTime(startOfDay(subDays(now, 6)));
  const until = getUnixTime(endOfDay(now));

  try {
    // Fetch per-agent summaries (JSON)
    const agentList = agents.value || [];
    const agentPromises = agentList.map(agent =>
      Report.getSummary(since, until, 'agent', agent.id)
        .then(r => ({ ...r.data, id: agent.id, name: agent.available_name || agent.name, thumbnail: agent.thumbnail || '' }))
        .catch(() => null)
    );
    const agentResults = await Promise.all(agentPromises);
    agentReports.value = agentResults.filter(Boolean);

    // Fetch per-inbox summaries (JSON)
    const inboxList = inboxes.value || [];
    const inboxPromises = inboxList.map(inbox =>
      Report.getSummary(since, until, 'inbox', inbox.id)
        .then(r => ({ ...r.data, id: inbox.id, name: inbox.name }))
        .catch(() => null)
    );
    const inboxResults = await Promise.all(inboxPromises);
    inboxReports.value = inboxResults.filter(Boolean);
    // Fetch account summary (current vs previous month)
    const monthAgo = getUnixTime(startOfDay(subDays(now, 30)));
    try {
      const summaryResp = await Report.getSummary(monthAgo, until, 'account');
      if (summaryResp.data) {
        accountSummary.value = {
          current: summaryResp.data,
          previous: summaryResp.data.previous || {},
        };
      }
    } catch {}
  } catch (e) {
    console.warn('[SZ-Dashboard] Report fetch error:', e);
  }
}

function fetchVuexData() {
  const now = new Date();
  const since = getUnixTime(startOfDay(subDays(now, 6)));
  const until = getUnixTime(endOfDay(now));
  store.dispatch('fetchAccountConversationHeatmap', { metric: 'conversations_count', from: since, to: until, groupBy: 'hour' });
  store.dispatch('fetchAccountResolutionHeatmap', { metric: 'resolutions_count', from: since, to: until, groupBy: 'hour' });
  store.dispatch('fetchAccountConversationMetric');
  store.dispatch('fetchAgentConversationMetric');
  store.dispatch('fetchTeamConversationMetric');
}

async function fetchAll() {
  // Ensure agents and inboxes are loaded first (needed by fetchReportData)
  await Promise.all([
    store.dispatch('agents/get'),
    store.dispatch('inboxes/get'),
    store.dispatch('labels/get'),
  ]);
  fetchVuexData();
  await fetchReportData();
}

onMounted(fetchAll);
useLiveRefresh(fetchAll, 60000);
</script>

<template>
  <div class="sz-dashboard">
    <ReportHeader :header-title="$t('OVERVIEW_REPORTS.HEADER')" />

    <!-- Row 1: KPI Cards -->
    <SzKpiRow :summary="accountSummary" />

    <!-- Row 2: Tokens Card (MOCK - full width) -->
    <SzTokensCard />

    <!-- Row 3: Bots (MOCK) + Chart placeholder -->
    <div class="sz-grid-2">
      <SzBotsCard />
      <div class="sz-graph-card">
        <SzForceGraph />
      </div>
    </div>

    <!-- Row 4: Etiquetas + Tráfego da conversa -->
    <div class="sz-grid-2">
      <SzEtiquetasCard />
      <MetricCard header="Tráfego da conversa" show-download @download="exportTrafego">
        <SzOverviewLineChart :data="conversationData" color="#388E3C" label="Conversas" />
      </MetricCard>
    </div>

    <!-- Row 5: Tempo médio resposta + Conversas por agente -->
    <div class="sz-grid-2">
      <MetricCard header="Tempo médio de resposta" show-download @download="exportResponseTime">
        <SzHorizontalBarChart :items="responseTimeItems" :legend-items="[{ label: 'Minutos', color: '#FF5500' }]" />
      </MetricCard>
      <MetricCard header="Conversas por agente" show-download @download="exportAgentConversations">
        <SzHorizontalBarChart :items="agentItems" :legend-items="[{ label: 'Não atendidas', color: '#2C7B2C' }, { label: 'Abertas', color: '#7DBA80' }]" />
      </MetricCard>
    </div>

    <!-- Row 6: Volume inbox + Duração conversa -->
    <div class="sz-grid-2">
      <MetricCard header="Volume de mensagens por caixa de entrada" show-download @download="exportInboxVolume">
        <div class="sz-inbox-volume">
          <div class="sz-inbox-chart-area">
            <div class="sz-inbox-yaxis">
              <span v-for="tick in inboxYTicks" :key="tick">{{ tick }}</span>
            </div>
            <div class="sz-inbox-bars-grid">
              <div class="sz-inbox-gridlines">
                <div v-for="tick in inboxYTicks" :key="'g'+tick" class="sz-inbox-gridline" />
              </div>
              <div class="sz-inbox-bars">
                <div v-for="inbox in inboxVolumeData" :key="inbox.name" class="sz-inbox-bar-wrap">
                  <div class="sz-inbox-bar-col">
                    <span class="sz-inbox-bar-val">{{ inbox.value }}</span>
                    <div class="sz-inbox-bar" :style="{ height: Math.max((inbox.value / inboxYMax) * 100, 3) + '%' }" />
                  </div>
                  <div class="sz-inbox-bar-label">
                    <svg width="14" height="14" viewBox="0 0 17 16" fill="none"><path d="M15 3.75H12.5V1.25C12.5.918 12.368.6 12.134.366 11.9.132 11.582 0 11.25 0H1.25C.918 0 .6.132.366.366.132.6 0 .918 0 1.25V11.25c0 .118.034.233.097.332.063.1.152.18.258.23.106.051.225.07.341.057.117-.014.228-.06.319-.133L3.75 9.531v2.344c0 .332.132.649.366.884.235.234.552.366.884.366h7.312l2.922 2.361c.11.09.248.139.39.14.166 0 .325-.066.442-.183.118-.118.183-.276.183-.442V5c0-.332-.132-.65-.366-.884A1.251 1.251 0 0015 3.75zM3.324 8.264L1.25 9.941V1.25h10v6.875H3.717a.627.627 0 00-.393.139zM15 13.691l-2.074-1.677a.629.629 0 00-.39-.139H5V9.375h6.25c.332 0 .65-.132.884-.366.234-.235.366-.552.366-.884V5H15v8.691z" fill="currentColor"/></svg>
                    <span>{{ inbox.name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="!inboxVolumeData.length" class="sz-empty-chart">Sem dados</div>
          <div class="sz-inbox-legend"><span class="sz-inbox-legend-dot" style="background:#5EEAD4" /> Número de conversas</div>
        </div>
      </MetricCard>
      <MetricCard header="Tempo médio duração da conversa" show-download @download="exportDuration">
        <SzHorizontalBarChart :items="conversationDurationItems" :legend-items="[{ label: 'Minutos', color: '#3B82F6' }]" />
      </MetricCard>
    </div>
  </div>
</template>

<style scoped>
.sz-graph-card { background: #fff; border: 1px solid #D1D5DB; border-radius: 8px; padding: 16px; min-height: 0; max-height: 336px; display: flex; flex-direction: column; overflow: hidden; }
.sz-dashboard { display: flex; flex-direction: column; gap: 16px; padding-bottom: 24px; }
.sz-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; align-items: stretch; }
@media (max-width: 1024px) { .sz-grid-2 { grid-template-columns: 1fr; } }
.sz-inbox-volume { width: 100%; }
.sz-inbox-chart-area { display: flex; gap: 4px; width: 100%; }
.sz-inbox-yaxis { display: flex; flex-direction: column; justify-content: space-between; height: 220px; }
.sz-inbox-yaxis span { font-size: 10px; color: #6B7280; text-align: right; min-width: 30px; line-height: 1; }
.sz-inbox-bars-grid { flex: 1; position: relative; height: 220px; }
.sz-inbox-gridlines { position: absolute; inset: 0; display: flex; flex-direction: column; justify-content: space-between; pointer-events: none; }
.sz-inbox-gridline { height: 0; border-bottom: 1px dashed #E5E7EB; }
.sz-inbox-bars { display: flex; align-items: flex-end; justify-content: space-around; height: 100%; position: relative; z-index: 1; }
.sz-inbox-bar-wrap { display: flex; flex-direction: column; align-items: center; flex: 1; max-width: 120px; }
.sz-inbox-bar-col { display: flex; flex-direction: column; align-items: center; width: 100%; height: 220px; justify-content: flex-end; }
.sz-inbox-bar-val { font-size: 12px; font-weight: 600; color: #1a1a1a; margin-bottom: 4px; }
.sz-inbox-bar { width: 60%; min-width: 32px; max-width: 80px; background: #5EEAD4; border-radius: 6px 6px 0 0; transition: height 0.5s ease; }
.sz-inbox-bar-label { display: flex; align-items: center; gap: 4px; margin-top: 8px; font-size: 11px; color: #6B7280; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100px; }
.sz-inbox-bar-label svg { flex-shrink: 0; color: #9CA3AF; }
.sz-inbox-legend { display: flex; align-items: center; justify-content: flex-end; gap: 6px; margin-top: 16px; font-size: 11px; color: #6B7280; }
.sz-inbox-legend-dot { width: 10px; height: 10px; border-radius: 50%; }
.sz-empty-chart { display: flex; align-items: center; justify-content: center; height: 180px; color: #9CA3AF; font-size: 13px; }

</style>

<style>
/* Dark mode */
.dark .sz-graph-card { background: rgba(255,255,255,0.03); border-color: rgba(255,255,255,0.1); }
.dark .sz-inbox-bar-val { color: #e5e7eb; }
.dark .sz-inbox-bar { background: #2DD4BF; }
.dark .sz-inbox-gridline { border-color: rgba(255,255,255,0.06); }
.dark .sz-inbox-bar-label svg { color: #6B7280; }
.dark .sz-inbox-yaxis span { color: #6B7280; }
.dark .sz-inbox-bar-label { color: #9CA3AF; }
.dark .sz-inbox-legend { color: #9CA3AF; }
</style>
